import { error } from '@utils/logger'
import { getClerk } from '@utils/clerk'

export interface VenueDetails {
    venue_ref: string;
    name: string;
    short_code?: string;
}


async function authFetch(input: RequestInfo, init: RequestInit = {}): Promise<Response> {
    const initWithCreds: RequestInit = {
        ...init,
        credentials: 'include',
    }

    // If Clerk is signed in, include a bearer token for authentication.
    try {
        const clerk = await getClerk()
        if (clerk?.isSignedIn) {
            const clerkToken = await clerk.session.getToken()
            if (clerkToken) {
                const headers = new Headers(initWithCreds.headers || undefined)
                headers.set('Authorization', `Bearer ${clerkToken}`)
                initWithCreds.headers = headers
            }
        }
    } catch (e) {
        // best-effort; if Clerk isn't available we proceed without the token
    }

    return await fetch(input, initWithCreds)
}

export async function fetchVenueCodeFromShortCode(shortCode: string): Promise<string> {
    try {
        const response = await authFetch(`/api/shortcode/${encodeURIComponent(shortCode)}`);
        if (!response.ok) {
            throw new Error('Failed to resolve venue code');
        }
        const data = await response.json();
        return data?.code;
    } catch (err) {
        error('Error fetching venue code from short code:', err);
        throw err;
    }
}

export type VenueBook = {
    logbook_ref: string
    name: string
    description?: string | null
    enabled_tag_ids?: string[]
    allow_date_selection?: boolean
    allow_text_search?: boolean
}

export type VenueTemplateItem = {
    template: string
    name: string
    logbook: string
    initial_state?: any
    auto_submit?: boolean
}

export type VenueTag = {
    id: string
    label: string
    level?: number
    icon_name?: string
}

export type TemplateDefinition = {
    id: string
    name?: string
    initial_state?: any
    initial_step?: string
    auto_submit?: boolean
}

export type StaffItem = {
    staff_ref: string
    name: string
}

export type UserSummary = {
    user_ref: string
    display_name: string
    email?: string
}

export type StaffSummary = {
    staff_ref: string
    name: string
}

export type RelatedRecords = {
    staff: Record<string, StaffSummary>
    users: Record<string, UserSummary>
}

const EMPTY_RELATED: RelatedRecords = {
    staff: {},
    users: {},
}


export async function searchVenueStaff(tenant: string, venue: string, q: string, init: RequestInit = {}): Promise<StaffItem[]> {
    try {
        const response = await authFetch(`/api/recording/${tenant}/venues/${venue}/staff?q=${encodeURIComponent(q)}`, init)

        if (!response.ok) {
            throw new Error('Failed to search venue staff')
        }

        const data = await response.json()

        // Expect an array of objects with at least `staff_ref` and `name`.
        return (data || []).map((s: any) => ({
            staff_ref: s.staff_ref || s.id || s.ref,
            name: s.name || ''
        }))
    } catch (err) {
        error('Error searching venue staff:', err)
        throw err
    }
}

export type EntryDetails = {
    record_ref?: string
    occured_at: string
    tags: string[]
    recorded_by_user_ref: string
    recorded_by_user?: UserSummary
    reported_by_staff_ref: string
    reported_by_staff?: {
        staff_ref: string
        name: string
    }
    details: string
    individuals_involved: string
    staff_actions: string
    police_involved: boolean
    incident_number: string
    police_incident_number?: string
    signed_off?: boolean
    struck_off?: boolean
}

export type ResponseItem = {
    details: EntryDetails
    sort_key: number
}

export interface VenueBootstrapData {
    me: any
    venue_details: VenueDetails
    templates: VenueTemplateItem[]
    tags: VenueTag[]
    books: VenueBook[]
    latest: EntryDetails[]
}

export async function fetchVenueBootstrap(tenant: string, venue: string): Promise<VenueBootstrapData> {
    try {
        const response = await authFetch(`/api/recording/${tenant}/venues/${venue}/bootstrap`)

        if (!response.ok) {
            throw new Error('Failed to fetch bootstrap venue data')
        }

        const data = await response.json()
        return data as VenueBootstrapData
    } catch (err) {
        error('Error fetching venue bootstrap data:', err)
        throw err
    }
}

export async function submitEntry(tenant: string, venue: string, book: string, entry: EntryDetails) {
    try {
        const response = await authFetch(`/api/recording/${tenant}/venues/${venue}/books/${book}/entries`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(entry)
        })

        if (!response.ok) {
            const txt = await response.text()
            throw new Error(`Submit failed: ${response.status} ${txt}`)
        }

        return await response.json()
    } catch (err) {
        error('Error submitting entry', err)
        throw err
    }
}

export async function searchBook(
    tenant: string,
    venue: string,
    book: string,
    q: string,
    init: RequestInit = {}
): Promise<EntryDetails[]> {
    const trimmed = q.trim()
    const url = trimmed
        ? `/api/recording/${tenant}/venues/${venue}/books/${encodeURIComponent(book)}/search?q=${encodeURIComponent(trimmed)}`
        : `/api/recording/${tenant}/venues/${venue}/books/${encodeURIComponent(book)}/search`

    try {
        const response = await authFetch(url, init)
        if (!response.ok) {
            throw new Error(`Search failed: ${response.status}`)
        }
        const json = await response.json()
        // Server now returns { data: [...], related: { staff, users } }
        const items: any[] = json.data || json
        const related: RelatedRecords | undefined = json.related
        return items.map((item) => mapEntryItem(item, related ?? { staff: {}, users: {} }))
    } catch (err) {
        error('Error searching book:', err)
        throw err
    }
}

export type ListEntriesResponse = {
    cursor: string
    data: {
        record_ref: string
        sort_key: string
        json_payload: ResponseItem
    }[],
    related?: RelatedRecords
}

function mapEntryItem(item: EntryDetails, related: RelatedRecords): EntryDetails {
    const d = item

    const staffRef: string = d.reported_by_staff_ref || ''
    const userRef: string = d.recorded_by_user_ref || ''

    const s = staffRef ? related.staff[staffRef] : undefined
    const reportedByStaff = s ? { staff_ref: s.staff_ref, name: s.name } : undefined
    // Resolve user from related dict.
    let recordedByUser: UserSummary | undefined = undefined
    if (userRef && related?.users?.[userRef]) {
        recordedByUser = related.users[userRef]
    }

    return {
        record_ref: d.record_ref,
        occured_at: d.occured_at || '',
        tags: d.tags || [],
        recorded_by_user_ref: userRef,
        recorded_by_user: recordedByUser,
        reported_by_staff_ref: staffRef,
        reported_by_staff: reportedByStaff,
        details: d.details || '',
        individuals_involved: d.individuals_involved || '',
        staff_actions: d.staff_actions || '',
        police_involved: !!d.police_involved,
        incident_number: d.incident_number || '',
        police_incident_number: d.police_incident_number,
        signed_off: d.signed_off,
        struck_off: d.struck_off,
    }
}

export function hydrateEntryDetails(entry: EntryDetails, related?: RelatedRecords): EntryDetails {
    return mapEntryItem(entry, related ?? EMPTY_RELATED)
}

export function hydrateResponseItem(item: ResponseItem, related?: RelatedRecords): ResponseItem {
    return {
        details: hydrateEntryDetails(item.details, related),
        sort_key: item.sort_key,
    }
}

export function getReporterDisplayName(entry: EntryDetails): string {
    const staffName = entry.reported_by_staff?.name?.trim()
    if (staffName) return staffName

    const userName = entry.recorded_by_user?.display_name?.trim()
    if (userName) return userName

    return 'Unknown'
}

export async function listBookEntries(
    tenant: string,
    venue: string,
    book: string,
    pageSize: number,
    cursor?: string,
    q?: string,
    init: RequestInit = {}
): Promise<ListEntriesResponse> {
    const params = new URLSearchParams()
    params.set('page_size', String(pageSize))
    if (cursor) params.set('cursor', cursor)
    if (q && q.trim()) params.set('q', q.trim())

    const url = `/api/recording/${tenant}/venues/${venue}/books/${encodeURIComponent(book)}/list?${params.toString()}`
    try {
        const response = await authFetch(url, init)
        if (!response.ok) {
            throw new Error(`List failed: ${response.status}`)
        }
        const json = await response.json()
        const related: RelatedRecords | undefined = json.related
        return {
            cursor: json.cursor || '',
            data: json.data,
            related,
        }
    } catch (err) {
        error('Error listing book entries:', err)
        throw err
    }
}

export async function updateEntry(
    tenant: string,
    venue: string,
    book: string,
    record_ref: string,
    entry: EntryDetails
): Promise<void> {
    const response = await authFetch(
        `/api/recording/${tenant}/venues/${venue}/books/${encodeURIComponent(book)}/entries/${encodeURIComponent(record_ref)}`,
        {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(entry),
        }
    )
    if (!response.ok) {
        const txt = await response.text()
        throw new Error(`Update failed: ${response.status} ${txt}`)
    }
}

export type RecordHistoryVersion = {
    seq: number
    payload: {
        details: EntryDetails
        signoff?: any
        strikeoff?: any
    }
}

export type RecordHistoryResponse = {
    versions: RecordHistoryVersion[]
    total_versions: number
}

export async function fetchEntryHistory(
    tenant: string,
    venue: string,
    book: string,
    record_ref: string,
    init: RequestInit = {}
): Promise<RecordHistoryResponse> {
    const response = await authFetch(
        `/api/recording/${tenant}/venues/${venue}/books/${encodeURIComponent(book)}/entries/${encodeURIComponent(record_ref)}/history`,
        init
    )
    if (!response.ok) {
        const txt = await response.text()
        throw new Error(`History fetch failed: ${response.status} ${txt}`)
    }
    return await response.json()
}